import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import axiosInstance from './axiosInstance';
import { getUser, saveUser } from './authHelper';

const POLL_INTERVAL = 8_000; // 8 saniye

// /auth/me için interceptor'sız ayrı instance — döngü önlemi
const plainAxios = axios.create({ baseURL: axiosInstance.defaults.baseURL });
plainAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PermissionContext = createContext({ permissions: [], isSuperAdmin: false, refresh: () => {} });

export const PermissionProvider = ({ children }) => {
  const init = getUser();
  const [permissions,  setPermissions]  = useState(init?.permissions  || []);
  const [isSuperAdmin, setIsSuperAdmin] = useState(init?.isSuperAdmin || false);
  const timerRef = useRef(null);

  /* Sırasız karşılaştırma — [a,b] ile [b,a] aynı kabul edilsin */
  const permsEqual = (a, b) =>
    a.length === b.length && [...a].sort().join() === [...b].sort().join();

  /* İzinleri state ve localStorage'a uygula */
  const applyPerms = useCallback((perms) => {
    if (perms.includes('*')) {
      setIsSuperAdmin(true);
      return;
    }
    setPermissions(prev => permsEqual(prev, perms) ? prev : [...perms]);
    const user = getUser();
    if (user) saveUser({ ...user, permissions: perms });
  }, []);

  /* /auth/me çek — plainAxios kullan, interceptor döngüsü yok */
  const fetchMe = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const res = await plainAxios.get('/auth/me');
      applyPerms(res.data.permissions || []);
      // twoFactorEnabled localStorage'a yaz
      const u = getUser();
      if (u && res.data.twoFactorEnabled !== undefined) {
        saveUser({ ...u, twoFactorEnabled: res.data.twoFactorEnabled });
      }
    } catch (e) {
      if (e.response?.status === 401) {
        // Token yeni yazılmış olabilir (race condition) — 3sn bekle, hâlâ 401 gelirse çıkış yap
        setTimeout(async () => {
          if (!localStorage.getItem('token')) return;
          try {
            await plainAxios.get('/auth/me');
          } catch (e2) {
            if (e2.response?.status === 401) {
              localStorage.removeItem('token');
              localStorage.removeItem('adminUser');
              window.location.href = '/login';
            }
          }
        }, 3000);
      }
    }
  }, [applyPerms]);

  /* Polling başlat/durdur */
  const startPolling = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(fetchMe, POLL_INTERVAL);
  }, [fetchMe]);

  const stopPolling = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  /* Mount + visibility */
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) { stopPolling(); }
      else { fetchMe(); startPolling(); }
    };
    document.addEventListener('visibilitychange', onVisibility);
    fetchMe();
    startPolling();
    // Login'den sonra token yazılınca hemen güncelle
    const onTokenUpdate = () => setTimeout(fetchMe, 200);
    window.addEventListener('tokenUpdated', onTokenUpdate);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('tokenUpdated', onTokenUpdate);
      stopPolling();
    };
  }, [fetchMe, startPolling, stopPolling]);

  /* axiosInstance interceptor — diğer API yanıtlarından da header varsa güncelle */
  useEffect(() => {
    const id = axiosInstance.interceptors.response.use(
      (response) => {
        const raw = response.headers['x-user-permissions'];
        if (raw) { try { applyPerms(JSON.parse(raw)); } catch { /* yoksay */ } }
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('adminUser');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
    return () => axiosInstance.interceptors.response.eject(id);
  }, [applyPerms]);

  const refresh = useCallback(() => fetchMe(), [fetchMe]);

  return (
    <PermissionContext.Provider value={{ permissions, isSuperAdmin, refresh }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions   = () => useContext(PermissionContext);
export const useHasPermission = (perm) => {
  const { permissions, isSuperAdmin } = usePermissions();
  return isSuperAdmin || permissions.includes('*') || permissions.includes(perm);
};
